/**
 * Dizarte Vehicle & Connector Compatibility Finder.
 *
 * Built on Horizon's own web-component base class (`@theme/component`), so it
 * participates in Horizon's ref tracking, declarative `on:` event delegation and
 * Section Rendering API re-renders exactly like `variant-picker` or `facets`.
 *
 * Progressive enhancement: the markup is a real GET <form> whose action points at
 * the target collection, so with JS off Shopify's Search & Discovery facets still
 * do the filtering. With JS on, make/model/year become dependent selects and the
 * submitted URL is built from the matched `vehicle` metaobject entries.
 *
 * Data source: sibling <script type="application/json" ref="data"> rendered by
 * snippets/dz-finder-data.liquid (shop.metaobjects.vehicle).
 *
 * @typedef {object} Vehicle
 * @property {string} id
 * @property {string} handle
 * @property {string} make
 * @property {string} model
 * @property {number} yearFrom
 * @property {number} yearTo
 * @property {string} connector
 * @property {number} kw
 */

import { Component } from '@theme/component';

/**
 * @extends {Component<{
 *   data: HTMLScriptElement,
 *   form: HTMLFormElement,
 *   connector?: HTMLSelectElement,
 *   make?: HTMLSelectElement,
 *   model?: HTMLSelectElement,
 *   year?: HTMLSelectElement,
 *   status?: HTMLElement,
 * }>}
 */
class VehicleFinderComponent extends Component {
  requiredRefs = ['data', 'form'];

  /** @type {Vehicle[]} */
  #vehicles = [];

  connectedCallback() {
    super.connectedCallback();

    this.#vehicles = this.#readData();

    this.#populateMakes();
    this.#restore();

    this.refs.form.addEventListener('submit', this.handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form?.removeEventListener('submit', this.handleSubmit);
  }

  /* ------------------------------------------------------------------ data */

  /** @returns {Vehicle[]} */
  #readData() {
    try {
      const parsed = JSON.parse(this.refs.data.textContent || '[]');
      return Array.isArray(parsed) ? parsed.filter((vehicle) => vehicle.make && vehicle.model) : [];
    } catch {
      // A malformed payload must not break the page: the plain form still submits.
      return [];
    }
  }

  get #targetUrl() {
    return this.dataset.targetUrl || '/collections/all';
  }

  get #connectorFilterKey() {
    return this.dataset.connectorFilterKey || 'filter.p.m.custom.connector_type';
  }

  get #vehicleFilterKey() {
    return this.dataset.vehicleFilterKey || 'filter.p.m.custom.compatible_vehicles';
  }

  get #maxYear() {
    return new Date().getFullYear() + 1;
  }

  /* --------------------------------------------------- declarative handlers */

  /** Triggered by `on:change="/handleMakeChange"`. */
  handleMakeChange = () => {
    this.#populateModels();
    this.#populateYears();
    this.#syncFromVehicle();
  };

  /** Triggered by `on:change="/handleModelChange"`. */
  handleModelChange = () => {
    this.#populateYears();
    this.#syncFromVehicle();
  };

  /** Triggered by `on:change="/handleYearChange"`. */
  handleYearChange = () => {
    this.#syncFromVehicle();
  };

  /**
   * Triggered by `on:change="/handleConnectorChange"`. Marks the connector as
   * merchant/shopper-chosen so vehicle matching stops overwriting it.
   */
  handleConnectorChange = () => {
    const { connector } = this.refs;
    if (connector) connector.dataset.userTouched = 'true';
  };

  /** Triggered by `on:click="/handleReset"`. */
  handleReset = (/** @type {Event} */ event) => {
    event.preventDefault();
    this.#reset();
  };

  /* ---------------------------------------------------- select population */

  /**
   * @param {HTMLSelectElement | undefined} select
   * @param {(string | number)[]} values
   */
  #setOptions(select, values) {
    if (!select) return;

    const current = select.value;
    const placeholder = select.dataset.placeholder || '';

    select.replaceChildren();

    const first = document.createElement('option');
    first.value = '';
    first.textContent = placeholder;
    select.appendChild(first);

    for (const value of values) {
      const option = document.createElement('option');
      option.value = String(value);
      option.textContent = String(value);
      select.appendChild(option);
    }

    if (values.map(String).includes(current)) select.value = current;
    select.disabled = values.length === 0;
  }

  #populateMakes() {
    const makes = this.#unique(this.#vehicles.map((vehicle) => vehicle.make)).sort((a, b) => a.localeCompare(b));

    this.#setOptions(this.refs.make, makes);
    this.#populateModels();
    this.#populateYears();
  }

  #populateModels() {
    const make = this.refs.make?.value ?? '';
    const models = this.#unique(
      this.#vehicles.filter((vehicle) => !make || vehicle.make === make).map((vehicle) => vehicle.model)
    ).sort((a, b) => a.localeCompare(b));

    this.#setOptions(this.refs.model, models);
  }

  #populateYears() {
    if (!this.refs.year) return;

    const years = new Set();

    for (const vehicle of this.#matches({ ignoreYear: true })) {
      const from = Number(vehicle.yearFrom) || 0;
      const to = Number(vehicle.yearTo) || this.#maxYear;
      if (!from) continue;
      for (let year = from; year <= Math.min(to, this.#maxYear); year += 1) years.add(year);
    }

    this.#setOptions(
      this.refs.year,
      Array.from(years).sort((a, b) => b - a)
    );
  }

  /**
   * @param {(string | undefined)[]} list
   * @returns {string[]}
   */
  #unique(list) {
    return Array.from(new Set(list.filter((value) => Boolean(value))));
  }

  /* ------------------------------------------------------------- matching */

  /**
   * @param {{ ignoreYear?: boolean }} [options]
   * @returns {Vehicle[]}
   */
  #matches(options = {}) {
    const make = this.refs.make?.value ?? '';
    const model = this.refs.model?.value ?? '';
    const year = options.ignoreYear ? 0 : Number(this.refs.year?.value ?? 0);

    return this.#vehicles.filter((vehicle) => {
      if (make && vehicle.make !== make) return false;
      if (model && vehicle.model !== model) return false;

      if (year) {
        const from = Number(vehicle.yearFrom) || 0;
        const to = Number(vehicle.yearTo) || this.#maxYear;
        if (from && year < from) return false;
        if (year > to) return false;
      }

      return true;
    });
  }

  /**
   * Auto-selects the connector when every match agrees on one, and narrates the
   * current match in the live region.
   */
  #syncFromVehicle() {
    const matches = this.#matches();
    const { connector, status } = this.refs;

    if (connector && !connector.dataset.userTouched) {
      const connectors = this.#unique(matches.map((vehicle) => vehicle.connector));
      const [only] = connectors;

      if (connectors.length === 1 && only) {
        const option = Array.from(connector.options).find((candidate) => candidate.value === only);
        if (option) connector.value = only;
      }
    }

    if (!status) return;

    const [first] = matches;

    if (!first) {
      status.textContent = '';
      return;
    }

    const label = [first.make, first.model].filter(Boolean).join(' ');
    const kw = Number(first.kw) || 0;
    const matched = this.#t('matched', { vehicle: label });

    status.textContent = kw ? `${matched} · ${this.#t('kw', { kw })}` : matched;
  }

  /**
   * Reads translated templates off the status element's data attributes, which
   * Liquid fills with `| t` output.
   *
   * @param {'matched' | 'kw' | 'noResults'} key
   * @param {Record<string, string | number>} [params]
   * @returns {string}
   */
  #t(key, params = {}) {
    const status = this.refs.status;
    const templates = {
      matched: status?.dataset.stringMatched || 'Showing parts for {{ vehicle }}',
      kw: status?.dataset.stringKw || 'Onboard charger: {{ kw }} kW',
      noResults: status?.dataset.stringNoResults || 'No matching parts yet.',
    };

    return Object.entries(params).reduce(
      (out, [name, value]) => out.replace(`{{ ${name} }}`, String(value)),
      templates[key]
    );
  }

  /* --------------------------------------------------------------- submit */

  handleSubmit = (/** @type {Event} */ event) => {
    const matches = this.#matches();
    const connector = this.refs.connector?.value ?? '';
    const hasVehicleSelection = Boolean(this.refs.make?.value || this.refs.model?.value);

    // Nothing chosen: let the plain form submit to the target collection.
    if (!connector && !hasVehicleSelection) return;

    event.preventDefault();

    if (hasVehicleSelection && matches.length === 0) {
      if (this.refs.status) this.refs.status.textContent = this.#t('noResults');
      return;
    }

    const url = new URL(this.#targetUrl, window.location.origin);

    if (connector) url.searchParams.append(this.#connectorFilterKey, connector);

    for (const vehicle of matches.slice(0, 12)) {
      if (vehicle.id) url.searchParams.append(this.#vehicleFilterKey, vehicle.id);
    }

    this.#persist();
    window.location.assign(url.toString());
  };

  /* ---------------------------------------------------------- persistence */

  static storageKey = 'dizarte:finder';

  #persist() {
    try {
      window.localStorage.setItem(
        VehicleFinderComponent.storageKey,
        JSON.stringify({
          connector: this.refs.connector?.value ?? '',
          make: this.refs.make?.value ?? '',
          model: this.refs.model?.value ?? '',
          year: this.refs.year?.value ?? '',
        })
      );
    } catch {
      // Storage unavailable (private mode, quota) — non-fatal.
    }
  }

  #restore() {
    let saved = null;

    try {
      saved = JSON.parse(window.localStorage.getItem(VehicleFinderComponent.storageKey) || 'null');
    } catch {
      saved = null;
    }

    if (!saved) return;

    if (this.refs.make && saved.make) this.refs.make.value = saved.make;
    this.#populateModels();
    if (this.refs.model && saved.model) this.refs.model.value = saved.model;
    this.#populateYears();
    if (this.refs.year && saved.year) this.refs.year.value = saved.year;
    if (this.refs.connector && saved.connector) this.refs.connector.value = saved.connector;

    this.#syncFromVehicle();
  }

  #reset() {
    for (const select of [this.refs.connector, this.refs.make, this.refs.model, this.refs.year]) {
      if (select) select.value = '';
    }

    if (this.refs.connector) delete this.refs.connector.dataset.userTouched;

    this.#populateModels();
    this.#populateYears();

    if (this.refs.status) this.refs.status.textContent = '';

    try {
      window.localStorage.removeItem(VehicleFinderComponent.storageKey);
    } catch {
      // noop
    }
  }
}

if (!customElements.get('vehicle-finder-component')) {
  customElements.define('vehicle-finder-component', VehicleFinderComponent);
}

/**
 * Collapsible wrapper for the finder — used by the header trigger and by the
 * re-surfaced finder on collection pages. Plain disclosure semantics.
 *
 * @extends {Component<{ toggle: HTMLButtonElement }>}
 */
class VehicleFinderToggleComponent extends Component {
  requiredRefs = ['toggle'];

  /** Triggered by `on:click="/handleToggle"`. */
  handleToggle = () => {
    const panel = document.getElementById(this.dataset.panel || '');
    const { toggle } = this.refs;
    if (!panel) return;

    const open = toggle.getAttribute('aria-expanded') === 'true';

    toggle.setAttribute('aria-expanded', String(!open));
    panel.hidden = open;

    if (open) return;

    /** @type {HTMLSelectElement | null} */
    const firstSelect = panel.querySelector('select:not([disabled])');
    firstSelect?.focus();
    panel.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };
}

if (!customElements.get('vehicle-finder-toggle-component')) {
  customElements.define('vehicle-finder-toggle-component', VehicleFinderToggleComponent);
}

/**
 * Header trigger behaviour for the Compatibility Finder.
 *
 * The wrapped <a> stays the fallback (no JS, crawlers, middle-click). With JS we
 * intercept plain left clicks so the highest-priority conversion element is
 * reachable *in place* from any template:
 *
 * 1. An inline Finder on this page (homepage section, or the collection-page
 *    block) wins: scroll to it, expand it when collapsed, focus its first select.
 * 2. Otherwise open the shared Finder inside Horizon's own <theme-drawer>
 *    (snippets/dz-finder-drawer.liquid) — same chrome as the cart drawer.
 * 3. If neither exists, do nothing and let the anchor navigate.
 *
 * @extends {Component<{ link: HTMLAnchorElement }>}
 */
class DizarteFinderTriggerComponent extends Component {
  requiredRefs = ['link'];

  /** Triggered by `on:click="/handleClick"`. */
  handleClick = (/** @type {MouseEvent} */ event) => {
    // Respect new-tab / new-window / download intents: let the browser navigate.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    if (this.#revealInline() || this.#openDrawer()) event.preventDefault();
  };

  /**
   * @returns {boolean} Whether an inline Finder was revealed on this page.
   */
  #revealInline() {
    /** @type {HTMLElement | null} */
    const panel = document.querySelector('vehicle-finder-component:not([data-in-drawer])');
    if (!panel) return false;

    const toggle = document.querySelector(
      `vehicle-finder-toggle-component[data-panel="${panel.id}"] [aria-expanded]`
    );

    if (panel.hidden && toggle instanceof HTMLElement) {
      // Reuse the disclosure's own handler so aria-expanded stays in sync.
      toggle.click();
    } else {
      panel.hidden = false;
      /** @type {HTMLSelectElement | null} */
      const firstSelect = panel.querySelector('select:not([disabled])');
      firstSelect?.focus();
      panel.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    return true;
  }

  /**
   * @returns {boolean} Whether the Finder drawer was opened.
   */
  #openDrawer() {
    /** @type {(HTMLElement & { open?: (options?: object) => void }) | null} */
    const drawer = document.getElementById(this.dataset.drawer || 'dz-finder-drawer');
    if (typeof drawer?.open !== 'function') return false;

    drawer.open();

    // Focus the first usable select once the drawer's own focus handling settles.
    requestAnimationFrame(() => {
      /** @type {HTMLSelectElement | null} */
      const firstSelect = drawer.querySelector('select:not([disabled])');
      firstSelect?.focus();
    });

    return true;
  }
}

if (!customElements.get('dz-finder-trigger')) {
  customElements.define('dz-finder-trigger', DizarteFinderTriggerComponent);
}
